import React from 'react';
import {
  Box,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Button,
  IconButton,
  Chip,
  Divider,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DATA_SOURCE_REF, isDynamic, DataSourceModeToggle, DynamicDataSourceField } from '../dataSourceBinding';
import { WaLabel, optionalLabel } from '../whatsappPreview';
import { FlowComponent } from '../../../../flow-core/FlowComponent';
import { ChipsSelectorCore } from '../../../../flow-core/components/ChipsSelectorCore';

// Auto-generate a stable, unique chip id from its human-readable title so users
// never have to author internal ids by hand. Slug the title (fallback "chip")
// and append a counter until unique within the list. Generated once at add-time.
const slugify = (text) =>
  String(text || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const generateOptionId = (title, existingIds) => {
  const base = slugify(title) || 'chip';
  let candidate = base;
  let n = 2;
  while (existingIds.includes(candidate)) {
    candidate = `${base}_${n}`;
    n += 1;
  }
  return candidate;
};

export const ChipsSelectorComponent = new FlowComponent({
  ...ChipsSelectorCore,
  renderPreview(properties) {
    var initValues = properties['init-value'] || [];
    var max = Number(properties['max-selected-items']) || 0;

    // As Meta's preview draws it (2026-10-10): the label with "(optional)", the description, a
    // "Choose up to N options." line, then outlined pills with bold text.
    return (
      <Box sx={{ opacity: properties.visible === false ? 0.5 : 1, mt: 2 }}>
        <WaLabel>{optionalLabel(properties.label || 'Select options', properties.required)}</WaLabel>
        {properties.description && (
          <Typography sx={{ fontSize: 14, color: 'text.secondary', mt: -0.75 }}>{properties.description}</Typography>
        )}
        {max > 0 && (
          <Typography sx={{ fontSize: 14, color: 'text.secondary', mt: 1 }}>
            {`Choose up to ${max} option${max === 1 ? '' : 's'}.`}
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.25 }}>
          {isDynamic(properties) ? (
            <Chip label={`⚡ options from ${properties[DATA_SOURCE_REF]}`} variant="outlined" disabled />
          ) : (
            (properties['data-source'] || []).map((chip) => (
              <Chip
                key={chip.id}
                label={chip.title}
                variant={initValues.includes(chip.id) ? 'filled' : 'outlined'}
                color={initValues.includes(chip.id) ? 'primary' : 'default'}
                disabled={properties.enabled === false || chip.enabled === false}
                sx={{ fontWeight: 600, fontSize: 14, height: 34, borderRadius: 999, px: 0.5 }}
              />
            ))
          )}
        </Box>
      </Box>
    );
  },
  renderPropertyEditor(properties, onChange, context) {
    var isDynamicFlow = context?.flowType === 'DYNAMIC' || context?.isDynamicFlow;

    var handleArrayItemChange = (index, field, value) => {
      var currentArray = properties['data-source'] || [];
      var newArray = [...currentArray];
      newArray[index] = { ...newArray[index], [field]: value };
      onChange('data-source', newArray);
    };

    var handleAddArrayItem = () => {
      var currentArray = properties['data-source'] || [];
      var title = 'New Chip';
      var newItem = { id: generateOptionId(title, currentArray.map((o) => o.id)), title, enabled: true };
      onChange('data-source', [...currentArray, newItem]);
    };

    var handleRemoveArrayItem = (index) => {
      var currentArray = properties['data-source'] || [];
      var newArray = currentArray.filter((_, i) => i !== index);
      onChange('data-source', newArray);
    };

    var handleInitValueToggle = (chipId) => {
      var currentInit = properties['init-value'] || [];
      if (currentInit.includes(chipId)) {
        onChange('init-value', currentInit.filter((id) => id !== chipId));
      } else {
        onChange('init-value', [...currentInit, chipId]);
      }
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          fullWidth
          label="Field Name"
          value={properties.name || ''}
          onChange={(e) => onChange('name', e.target.value)}
          required
          size="small"
          helperText="Used to identify this field in the form data"
        />

        <TextField
          fullWidth
          label="Label"
          value={properties.label || ''}
          onChange={(e) => onChange('label', e.target.value)}
          required
          size="small"
          inputProps={{ maxLength: 80 }}
          helperText={`${(properties.label || '').length}/80 characters`}
        />

        <TextField
          fullWidth
          label="Description"
          value={properties.description || ''}
          onChange={(e) => onChange('description', e.target.value)}
          size="small"
          multiline
          rows={2}
          inputProps={{ maxLength: 300 }}
          helperText={`${(properties.description || '').length}/300 characters`}
        />

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" color="text.secondary">
          Selection Limits
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            label="Min Selected"
            type="number"
            value={properties['min-selected-items'] || ''}
            onChange={(e) => onChange('min-selected-items', e.target.value)}
            size="small"
            sx={{ flex: 1 }}
            inputProps={{ min: 0 }}
          />
          <TextField
            label="Max Selected"
            type="number"
            value={properties['max-selected-items'] || ''}
            onChange={(e) => onChange('max-selected-items', e.target.value)}
            size="small"
            sx={{ flex: 1 }}
            inputProps={{ min: 1 }}
          />
        </Box>

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" color="text.secondary">
          Chips (2-20 options)
        </Typography>

        {isDynamicFlow && <DataSourceModeToggle properties={properties} onChange={onChange} />}

        {isDynamicFlow && isDynamic(properties) ? (
          <DynamicDataSourceField properties={properties} onChange={onChange} />
        ) : (
          <Box>
            {(properties['data-source'] || []).map((item, index) => (
              <Box
                key={index}
                sx={{ mb: 1, p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}
              >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                  <TextField
                    size="small"
                    label="Title"
                    value={item.title || ''}
                    onChange={(e) => handleArrayItemChange(index, 'title', e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveArrayItem(index)}
                    color="error"
                    disabled={(properties['data-source'] || []).length <= 2}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={item.enabled !== false}
                        onChange={(e) => handleArrayItemChange(index, 'enabled', e.target.checked)}
                      />
                    }
                    label="Enabled"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={(properties['init-value'] || []).includes(item.id)}
                        onChange={() => handleInitValueToggle(item.id)}
                      />
                    }
                    label="Pre-selected"
                  />
                </Box>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddArrayItem}
              variant="outlined"
              disabled={(properties['data-source'] || []).length >= 20}
            >
              Add Chip
            </Button>
          </Box>
        )}

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" color="text.secondary">
          Validation
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={properties.required || false}
              onChange={(e) => onChange('required', e.target.checked)}
            />
          }
          label="Required"
        />

        <TextField
          fullWidth
          label="Error Message"
          value={properties['error-message'] || ''}
          onChange={(e) => onChange('error-message', e.target.value)}
          size="small"
          helperText="Custom error message when validation fails"
        />

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" color="text.secondary">
          State
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={properties.enabled !== false}
              onChange={(e) => onChange('enabled', e.target.checked)}
            />
          }
          label="Enabled"
        />

        <FormControlLabel
          control={
            <Switch
              checked={properties.visible !== false}
              onChange={(e) => onChange('visible', e.target.checked)}
            />
          }
          label="Visible"
        />
      </Box>
    );
  },
});
